import { forwardRef, useId } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Icon } from './Icon';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  label: string;
  hideLabel?: boolean;
  name: string;
  options: RadioOption[];
  value: string | null;
  onChange: (value: string) => void;
  orientation?: 'vertical' | 'horizontal';
  error?: string | null;
}

/**
 * Native radios in a labelled group: arrow-key roving focus is provided by the
 * browser once the inputs share a `name`, so no roving-tabindex code is needed.
 */
export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  {
    label,
    hideLabel,
    name,
    options,
    value,
    onChange,
    orientation = 'vertical',
    error,
    className,
    ...rest
  },
  ref,
) {
  const baseId = useId();
  const errorId = error ? `${baseId}-error` : undefined;

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-labelledby={`${baseId}-label`}
      aria-describedby={errorId}
      aria-invalid={Boolean(error) || undefined}
      className={cn('flex flex-col gap-2', className)}
      {...rest}
    >
      <span
        id={`${baseId}-label`}
        className={cn('text-dense-lg font-medium text-content-secondary', hideLabel && 'sr-only')}
      >
        {label}
      </span>
      <div className={cn('flex gap-3', orientation === 'vertical' ? 'flex-col' : 'flex-wrap')}>
        {options.map((option) => {
          const optionId = `${baseId}-${option.value}`;
          return (
            <div key={option.value} className="flex items-start gap-2">
              <input
                id={optionId}
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                disabled={option.disabled}
                onChange={() => onChange(option.value)}
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0 cursor-pointer border-line-strong accent-[var(--color-action)]',
                  'disabled:cursor-not-allowed',
                  focusRing,
                )}
              />
              <div className="flex flex-col">
                <label htmlFor={optionId} className="cursor-pointer text-dense-lg text-content">
                  {option.label}
                </label>
                {option.description ? (
                  <span className="text-dense text-content-muted">{option.description}</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {error ? (
        <p id={errorId} className="flex items-center gap-1 text-dense text-danger">
          <Icon name="alert-circle" size={13} />
          {error}
        </p>
      ) : null}
    </div>
  );
});
